// App logs tab (v0.3.4) — admin view of device DiagLog snapshots
// uploaded from each user's Android wrapper via Settings →
// Diagnostics → "Upload to server".
//
// Left pane: list of recent uploads (one row per device, most-recent
// first). Right pane: the snapshot body for whichever device row is
// selected.
//
// Storage: one row per (user_id, device_id); a fresh upload replaces
// the prior snapshot. See `device_logs` schema + diag.* RPCs in
// src/web_server.mjs + src/db.mjs.

import { useEffect, useState } from "preact/hooks";
import { api, type DeviceLog, type DeviceLogSummary } from "../../lib/rpc";

interface DeviceLogsTabProps { notify: (msg: string) => void; }

function formatBytes(n: number): string {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatTimestamp(iso: string): string {
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

export function AdminDeviceLogsTab({ notify }: DeviceLogsTabProps) {
  const [list, setList] = useState<DeviceLogSummary[] | null>(null);
  const [active, setActive] = useState<DeviceLog | null>(null);
  const [loadingActive, setLoadingActive] = useState(false);

  const refresh = async () => {
    try {
      const l = await api.adminListDeviceLogs(100);
      setList(l);
      // Pre-select the most recent entry so the right pane isn't empty.
      if (l.length > 0 && active == null) {
        void selectRow(l[0].id);
      }
    } catch (e: unknown) {
      notify((e as Error)?.message || "Couldn't load device logs");
      setList([]);
    }
  };

  const selectRow = async (id: number) => {
    setLoadingActive(true);
    try {
      const row = await api.adminGetDeviceLog(id);
      setActive(row);
    } catch (e: unknown) {
      notify((e as Error)?.message || "Couldn't load log body");
    } finally {
      setLoadingActive(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onCopy = async () => {
    if (!active) return;
    try {
      await navigator.clipboard.writeText(active.snapshot);
      notify("Copied to clipboard");
    } catch {
      notify("Copy failed — select the text manually");
    }
  };

  return (
    <div className="admin-card">
      <h3>App logs</h3>
      <p className="admin-card-sub">
        DiagLog snapshots uploaded from each user's Android wrapper. Each
        device's most recent upload overwrites any prior one (single row
        per device). Useful for debugging FCM registration, push
        delivery, or any other on-device async behaviour without asking
        the user to paste from their phone.
      </p>

      <div className="device-logs-actions">
        <button type="button" className="settings-btn" onClick={refresh}>
          Refresh
        </button>
        {active && (
          <button type="button" className="settings-btn" onClick={onCopy}>
            Copy snapshot
          </button>
        )}
      </div>

      <div className="device-logs-layout">
        <div className="device-logs-list">
          {list === null && <div className="admin-empty">Loading…</div>}
          {list && list.length === 0 && (
            <div className="admin-empty">
              No device logs yet. Have a user open the Android app's
              Settings → Diagnostics page and tap "Upload to server".
            </div>
          )}
          {list && list.map((row) => (
            <button
              key={row.id}
              type="button"
              className="device-logs-row"
              data-active={active?.id === row.id}
              onClick={() => selectRow(row.id)}
            >
              <div className="device-logs-row-top">
                <span className="device-logs-username">{row.username || row.userId}</span>
                <span className="device-logs-ts">{formatTimestamp(row.createdAt)}</span>
              </div>
              <div className="device-logs-row-bot">
                <span className="device-logs-device">device {row.deviceId.slice(0, 8)}…</span>
                <span className="device-logs-version">{row.versionName || "?"}</span>
                <span className="device-logs-bytes">{formatBytes(row.snapshotBytes)}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="device-logs-viewer">
          {!active && !loadingActive && (
            <div className="admin-empty">Select a row to view its snapshot.</div>
          )}
          {loadingActive && <div className="admin-empty">Loading…</div>}
          {active && (
            <>
              <header className="device-logs-meta">
                <div><strong>{active.username || active.userId}</strong></div>
                <div className="device-logs-meta-sub">
                  device <code>{active.deviceId}</code> · {active.versionName || "?"} · uploaded {formatTimestamp(active.createdAt)}
                </div>
              </header>
              <textarea
                className="device-logs-body"
                readOnly
                spellcheck={false}
                value={active.snapshot}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
